import {
    DateService,
    DetectorAccuracyLevel,
    DetectorEvent,
    FirstNavigation,
    PageEvent,
    PulseEnvironment,
    Tab,
    TabEvents,
    TabManagerService,
} from '@pulse/browser-shared';

import { TriggerEvent } from '@pulse/browser-tracker-shared';
import { PulseStorage, TrackerConfigurations, isToggleOn } from '@pulse/common';
import { ActivitySynchronizer } from '../activity.synchronizer';
import { WINDOW_INTERACTION_EVENTS } from '../activity.tracker';
import { TabInfoService } from './tab-info.service';

export class TabOpenerDetector {
    private opener: TriggerEvent | null = null;
    private firstNavigationKey = 'first_navigation';
    private firstNavigation = true;

    constructor(
        private tabInfoService: TabInfoService,
        private tabStorage: PulseStorage,
        private activitySyncService: ActivitySynchronizer,
        private tabManager: TabManagerService,
        private date: DateService,
        private env: PulseEnvironment,
        private config: TrackerConfigurations,
    ) {}

    getOpener(initial = false): TriggerEvent | null {
        const opener = this.opener || this.fetchOpener();
        const navigationJson = this.tabStorage.getJSON<FirstNavigation>(this.firstNavigationKey);
        const firstNavigation = navigationJson?.firstNavigation === undefined ? true : false;
        this.firstNavigation = firstNavigation;
        this.tabStorage.setJSON<FirstNavigation>(this.firstNavigationKey, {
            firstNavigation: false,
            lastNavigationCount: navigationJson?.lastNavigationCount || 0,
            lastNavigationUrl:
                initial && firstNavigation
                    ? this.env.win.location.href
                    : navigationJson?.lastNavigationUrl || '',
        });

        // if false it means user opened new tab by clicking
        // on the |+| button of their browser. In that case the page will
        // have an additional history item which is the `New Tab Page` / `Home Page`.
        const trusted =
            typeof this.env.win.history?.length !== 'number' || this.env.win.history.length === 1;

        const redirected = this.env.win.history.length > 1 && firstNavigation;
        const timeWindowRedirection = redirected ? 5000 : 0;
        const timeWindow = trusted
            ? this.config.tabOpenerDetectionTimeWindow
            : Math.max(
                  timeWindowRedirection,
                  Math.min(1000, this.config.tabOpenerDetectionTimeWindow / 2),
              );

        const minAccuracy = trusted
            ? DetectorAccuracyLevel.NoVisibility
            : DetectorAccuracyLevel.Focus;

        if (opener?.id) {
            return opener;
        }

        let openerId: string | null = null;

        if (this.env.win.opener) {
            // Use window.opener if available
            openerId = this.tabManager.getSiblingTabId({
                win: this.env.win.opener,
            } as PulseEnvironment);
        }
        let siblings;

        if (openerId) {
            siblings = this.tabInfoService.fetchSiblingTabs(openerId);
        } else {
            siblings = this.tabInfoService.fetchSiblingTabs();
        }

        const siblingEvents = siblings.map((s: Tab): TabEvents => {
            const events = this.activitySyncService.getTabEvents(s.id) || [];

            return {
                tabId: s.id,
                events,
            };
        });

        const openerEvent: DetectorEvent | null = this.detectOpener(
            this.env.win.document.referrer,
            siblingEvents,
            timeWindow,
            minAccuracy,
            redirected,
        );

        this.setOpener({
            id: openerEvent?.tabId || openerId,
            type: openerEvent?.type,
            url: openerEvent?.eventUrl,
            name: openerEvent?.section.name,
            group: openerEvent?.section.group,
            element: openerEvent?.element,
            popupComponent: openerEvent?.section?.popupComponent,
            popupComponentType: openerEvent?.section?.popupComponentType,
            additionalProperties: openerEvent?.additionalProperties,
        });

        const openerData = openerEvent?.tabId
            ? {
                  id: openerEvent?.tabId,
                  type: openerEvent?.type,
                  url: openerEvent?.eventUrl,
                  name: openerEvent?.section.name,
                  group: openerEvent?.section.group,
                  element: openerEvent?.element,
                  popupComponent: openerEvent?.section?.popupComponent,
                  popupComponentType: openerEvent?.section?.popupComponentType,
                  additionalProperties: openerEvent?.additionalProperties,
              }
            : null;

        return openerData;
    }

    isFirstNavigation(): boolean {
        return this.firstNavigation;
    }

    getTriggerEvent(initial = false): TriggerEvent | null {
        if (!isToggleOn(this.config.eventEnrichment, 'tab_id')) {
            return null;
        }
        const navigationJson = this.tabStorage.getJSON<FirstNavigation>(this.firstNavigationKey);
        const firstNavigation = navigationJson?.firstNavigation === undefined ? true : false;
        const lastNavigationCount = navigationJson?.lastNavigationCount || 0;
        this.tabStorage.setJSON<FirstNavigation>(this.firstNavigationKey, {
            firstNavigation: false,
            lastNavigationCount: this.env.win.history.length,
            lastNavigationUrl: this.env.win.location.href,
        });
        this.firstNavigation = firstNavigation;
        const redirected =
            this.env.win.history.length - lastNavigationCount > 1 && !firstNavigation;
        const timeWindow = redirected ? 5000 : initial ? 3500 : 500;

        const minAccuracy = DetectorAccuracyLevel.NoVisibility;

        const referrer = navigationJson?.lastNavigationUrl || this.env.win.document.referrer;
        const tab = this.tabInfoService.getCurrentTab();
        const events = this.activitySyncService.getTabEvents(tab.id) || [];

        const triggerEvent = this.detectTriggerEvent(
            referrer,
            events,
            timeWindow,
            minAccuracy,
            redirected,
            tab.id,
        );

        const triggerEventData = triggerEvent
            ? {
                  id: triggerEvent?.tabId,
                  type: triggerEvent?.type,
                  url: triggerEvent?.eventUrl,
                  name: triggerEvent?.section.name,
                  group: triggerEvent?.section.group,
                  popupComponent: triggerEvent?.section?.popupComponent,
                  popupComponentType: triggerEvent?.section?.popupComponentType,
                  element: triggerEvent?.element,
                  additionalProperties: triggerEvent?.additionalProperties,
              }
            : null;

        return triggerEventData;
    }

    private fetchOpener(): TriggerEvent | null {
        this.opener = this.tabStorage.getJSON<TriggerEvent>('tab_opener');

        return this.opener;
    }

    private detectOpener(
        referrer: string,
        siblingTabEvents: TabEvents[],
        timeWindow: number,
        minAccuracy: DetectorAccuracyLevel,
        redirected: boolean,
    ): DetectorEvent | null {
        const events = this.createDetectorEvents(referrer, siblingTabEvents).filter(
            (e) => e.timeDiff <= timeWindow,
        );

        if (!events.length) {
            return null;
        }

        for (let accuracy: DetectorAccuracyLevel = 1; accuracy <= minAccuracy; accuracy++) {
            const matches = this.filterAccuracyMatches(events, referrer, accuracy, redirected);

            if (
                matches.length &&
                (accuracy < DetectorAccuracyLevel.NoVisibility || this.countTabs(matches) === 1)
            ) {
                return matches[0] || null;
            }
        }

        return null;
    }

    private detectTriggerEvent(
        referrer: string,
        tabEvents: PageEvent[],
        timeWindow: number,
        minAccuracy: DetectorAccuracyLevel,
        redirected: boolean,
        tabId: string,
    ): DetectorEvent | null {
        const events = this.ctreateTriggerEvents(referrer, tabEvents, tabId).filter(
            (e) => e.timeDiff <= timeWindow,
        );

        if (!events.length) {
            return null;
        }

        for (let accuracy: DetectorAccuracyLevel = 1; accuracy <= minAccuracy; accuracy++) {
            const matches = this.filterAccuracyMatches(events, referrer, accuracy, redirected);

            if (matches.length && accuracy < DetectorAccuracyLevel.NoVisibility) {
                return matches[0] || null;
            }
        }

        return null;
    }

    private setOpener(opener: TriggerEvent) {
        this.opener = opener;

        return this.tabStorage.setJSON<TriggerEvent>('tab_opener', this.opener);
    }

    private filterAccuracyMatches(
        events: DetectorEvent[],
        referrer: string,
        level: DetectorAccuracyLevel,
        redirected: boolean,
    ): DetectorEvent[] {
        switch (level) {
            case DetectorAccuracyLevel.LinkInteraction:
                return this.filterLinkInteractionEvents(events, referrer);
            case DetectorAccuracyLevel.Link:
                return this.filterLinkEvents(events, referrer);
            case DetectorAccuracyLevel.Interaction:
                return this.filterInteractionEvents(events, referrer);
            case DetectorAccuracyLevel.Focus:
                return this.filterFocusedEvents(events, referrer, redirected);
            case DetectorAccuracyLevel.Visibility:
                return this.filterVisibleEvents(events, referrer);
            case DetectorAccuracyLevel.NoVisibility:
            default:
                return this.filterNoVisibility(events, referrer);
        }
    }

    private filterLinkInteractionEvents(events: DetectorEvent[], referrer: string) {
        return events.filter(
            (e) =>
                (referrer ? e.docURL.startsWith(referrer) : true) &&
                this.isLinkMatch(e) &&
                this.isInteractionEvent(e) &&
                e.docFocused &&
                e.docVisible,
        );
    }

    private filterLinkEvents(events: DetectorEvent[], referrer: string): DetectorEvent[] {
        return events.filter(
            (e) =>
                (referrer ? e.docURL.startsWith(referrer) : true) &&
                this.isLinkMatch(e) &&
                e.docFocused &&
                e.docVisible,
        );
    }

    private filterInteractionEvents(events: DetectorEvent[], referrer: string) {
        return events.filter(
            (e) =>
                (referrer ? e.docURL.startsWith(referrer) : true) &&
                this.isInteractionEvent(e) &&
                e.docFocused &&
                e.docVisible,
        );
    }

    private filterFocusedEvents(events: DetectorEvent[], referrer: string, redirected: boolean) {
        return events.filter((e) => {
            if (redirected) {
                return e.docFocused && e.docVisible;
            }

            return (
                (referrer ? e.docURL.startsWith(referrer) : true) && e.docFocused && e.docVisible
            );
        });
    }

    private filterVisibleEvents(events: DetectorEvent[], referrer: string) {
        return events.filter(
            (e) => (referrer ? e.docURL.startsWith(referrer) : true) && e.docVisible,
        );
    }

    private filterNoVisibility(events: DetectorEvent[], referrer: string): DetectorEvent[] {
        return events.filter((e) => (referrer ? e.docURL.startsWith(referrer) : true));
    }

    private isLinkMatch(e: DetectorEvent) {
        if (e.element && e.element.nodeName === 'A') {
            const href = e.element.attributes.find(
                (i: { name: string }) => i.name === 'href',
            )?.value;

            return href === this.config.initialDocURL;
        }

        return false;
    }

    private isInteractionEvent(e: DetectorEvent) {
        return WINDOW_INTERACTION_EVENTS.indexOf(e.type as any) !== -1;
    }

    private countTabs(events: DetectorEvent[]): number {
        return new Set(events.map((i) => i.tabId)).size;
    }

    private createDetectorEvents(referrer: string, tabs: TabEvents[]): DetectorEvent[] {
        const navigationStart = this.date.hrtTimeOrigin - 2;
        const events: DetectorEvent[] = [];

        for (const tab of tabs) {
            const tabEvents = tab.events.map(
                (event: PageEvent): DetectorEvent => ({
                    ...event,
                    tabId: tab.tabId,
                    timeDiff: Math.abs(navigationStart - event.ts),
                    referrerMatch: referrer ? event.docURL.startsWith(referrer) : true,
                    eventUrl: event.docURL,
                }),
            );

            events.push(...tabEvents);
        }

        return events.sort((a, b) => {
            if (a.timeDiff > b.timeDiff) {
                return 1;
            }
            if (a.timeDiff < b.timeDiff) {
                return -1;
            }

            return 0;
        });
    }

    private ctreateTriggerEvents(
        referrer: string,
        tabEvents: PageEvent[],
        tabId: string,
    ): DetectorEvent[] {
        const navigationStart = this.date.now() - 2;
        let events: DetectorEvent[] = [];
        events = tabEvents.map(
            (event): DetectorEvent => ({
                ...event,
                tabId,
                timeDiff: Math.abs(navigationStart - event.ts),
                referrerMatch: referrer ? event.docURL.startsWith(referrer) : true,
                eventUrl: event.docURL,
            }),
        );

        return events.sort((a, b) => {
            if (a.timeDiff > b.timeDiff) {
                return 1;
            }
            if (a.timeDiff < b.timeDiff) {
                return -1;
            }

            return 0;
        });
    }
}
